import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import type { plans } from '@/lib/client';
import { approveDisabledReason, needsWriteAccess } from '@/lib/plans';
import { cn } from '@/lib/utils';
import { useBrowserStore } from '@/stores/browserStore';
import { usePlansStore } from '@/stores/plansStore';
import { useWriteEnableStore } from '@/stores/writeEnableStore';
import { ScheduleDialog } from './ScheduleDialog';
import { WatchBrowserPrompt } from './WatchBrowserPrompt';

function Button({ children, onClick, disabled, title, primary, danger, busy }: { children: React.ReactNode; onClick?: () => void; disabled?: boolean; title?: string; primary?: boolean; danger?: boolean; busy?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || busy}
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        primary ? 'bg-primary text-primary-foreground hover:bg-primary/90' : danger ? 'border border-red-500/40 text-red-300 hover:bg-red-500/10' : 'border border-border text-foreground hover:bg-secondary',
      )}
    >
      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {children}
    </button>
  );
}

/**
 * The plan's controls by status (docs/plans/change-planner.md §6), the draft's Save/Discard first. A
 * plan waiting on write access (D3) leads with enabling it; one that is ready leads with "Approve &
 * run" (one approval, D3/D11), with plain Approve beside it for those who will schedule or run later.
 */
export function PlanControls({ plan }: { plan: plans.PlanView }) {
  const navigate = useNavigate();
  const { draft, busy, saveDraft, discardDraft, approve, run, cancel, revert, revertAgain, unschedule } = usePlansStore();
  const setPendingPlan = useWriteEnableStore((s) => s.setPendingPlan);
  const panelOpen = useBrowserStore((s) => s.panelOpen);
  const [askWatch, setAskWatch] = useState<{ run: boolean } | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const dirty = draft !== null && draft.planId === plan.id && draft.dirty;
  const hasBrowserSteps = plan.steps.some((step) => step.channel === 'browser');
  const approveWhy = approveDisabledReason(plan);
  const writeBlocked = needsWriteAccess(plan);
  const status = plan.status;

  const doApprove = (runNow: boolean, watchBrowser: boolean) => void approve(plan.id, { run: runNow, watchBrowser });
  const onApprove = (runNow: boolean) => {
    if (hasBrowserSteps && !panelOpen) setAskWatch({ run: runNow });
    else doApprove(runNow, false);
  };
  const onEnableWrite = () => {
    setPendingPlan({ planId: plan.id, ...(plan.missing_scopes.length > 0 ? { scopes: plan.missing_scopes } : {}) });
    navigate('/chat?setup=write');
  };
  const onRevert = async () => {
    const id = await revert(plan.id);
    if (id !== null) navigate(`/changes/${id}`);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {dirty && (
        <>
          <Button primary busy={busy.save} onClick={() => void saveDraft()}>Save changes</Button>
          <Button onClick={discardDraft}>Discard</Button>
          <span className="mx-1 h-5 w-px bg-border" />
        </>
      )}
      {(status === 'draft' || status === 'ready') && (
        <>
          {writeBlocked ? (
            <Button primary disabled={dirty} title="add the write scopes to your Zoom app in the browser, then come back to approve" onClick={onEnableWrite}>
              <Lock className="h-3.5 w-3.5" /> Enable write access
            </Button>
          ) : (
            <>
              <Button primary busy={busy.approve} disabled={dirty || approveWhy !== null} title={dirty ? 'save or discard your changes first' : (approveWhy ?? 'approve the plan and run every step now')} onClick={() => onApprove(true)}>
                Approve &amp; run
              </Button>
              <Button busy={busy.approve} disabled={dirty || approveWhy !== null} title={dirty ? 'save or discard your changes first' : (approveWhy ?? 'approve now; run or schedule it later')} onClick={() => onApprove(false)}>
                Approve
              </Button>
            </>
          )}
          <Button danger busy={busy.cancel} onClick={() => void cancel(plan.id)}>Cancel</Button>
        </>
      )}
      {status === 'approved' && (
        <>
          <Button primary busy={busy.run} disabled={dirty} onClick={() => void run(plan.id)}>Run now</Button>
          <Button disabled={dirty || !plan.schedulable.ok} title={plan.schedulable.ok ? 'run it later, on its own' : plan.schedulable.reason} onClick={() => setScheduling(true)}>Schedule</Button>
          <Button danger busy={busy.cancel} onClick={() => void cancel(plan.id)}>Cancel</Button>
        </>
      )}
      {status === 'scheduled' && (
        <>
          <span className="text-sm text-muted-foreground">Runs {plan.scheduled_at ? new Date(plan.scheduled_at).toLocaleString() : 'soon'}</span>
          <Button primary busy={busy.run} onClick={() => void run(plan.id)}>Run now</Button>
          <Button busy={busy.schedule} onClick={() => void unschedule(plan.id)}>Unschedule</Button>
          <Button danger busy={busy.cancel} onClick={() => void cancel(plan.id)}>Cancel</Button>
        </>
      )}
      {(status === 'running' || status === 'reverting') && (
        <Button danger busy={busy.cancel} disabled={status === 'reverting'} title={status === 'reverting' ? 'the revert finishes on its own' : 'stop, then revert what completed'} onClick={() => void cancel(plan.id)}>
          {status === 'reverting' ? 'Reverting…' : 'Stop and revert'}
        </Button>
      )}
      {status === 'completed' && !plan.reverted_by_plan_id && <Button danger busy={busy.revert} title="draft a plan that undoes every step, for you to approve" onClick={() => void onRevert()}>Revert</Button>}
      {status === 'revert_partial' && plan.steps.some((step) => step.run?.revert_status === 'failed') && (
        <Button busy={busy.revert} title="make again each undo that failed; what is left by hand stays yours" onClick={() => void revertAgain(plan.id)}>Try the undo again</Button>
      )}
      {askWatch !== null && (
        <WatchBrowserPrompt
          onAnswer={(watch) => {
            const { run: runNow } = askWatch;
            setAskWatch(null);
            doApprove(runNow, watch);
          }}
        />
      )}
      {scheduling && <ScheduleDialog plan={plan} onClose={() => setScheduling(false)} />}
    </div>
  );
}
